import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from "react-redux";
import {
  setDateRange,
  setModel,
  selectComparisonModels,
  setComparisonModels,
  setHorizonFilter,
} from "../slices/marketSlice";
import { selectAuthIsAdmin, selectAuthIsMarketOwner, selectAuthIsMarketOwnerReadOnly, selectAuthIsForecaster } from "../slices/authSlice";
import { setSelectedForecast } from "../slices/dataSlice";
import { selectUsersList } from "../slices/userSlice";
import { selectBlurSensitive } from "../slices/preferencesSlice";
import { selectCanUseInsights } from "../slices/insightsSlice";
import { ENSEMBLE_MODELS } from "../constants/ensembleModels";
import CustomButton from "./CustomButton";
import DateRangePicker from './DateRangePicker';
import { ForecasterPicker } from './ForecasterPicker';
import InsightsMenu from './insights/InsightsMenu';
import { SLIDE_OVER_TOP_OFFSET_STYLE } from './slideOverTransition';
import { resourceUnitMeta } from '../utils/resourceUnits';
import { CogIcon, XMarkIcon, UsersIcon, FunnelIcon, CalendarIcon, ClockIcon, ChevronDownIcon, EyeIcon } from '@heroicons/react/24/outline';

const formatHorizonLabel = (horizon) => (
  Number(horizon) === 0 ? 'Intraday' : `D+${horizon}`
);

// Compact pill used in the dashboard chart-header filter bar. Shows an
// optional leading icon, a muted label, the active value, an optional count
// badge, and a trailing chevron. `active` highlights it while its popover is
// open. Built to be visually consistent across all the dashboard filters.
function FilterChip({ icon: Icon, label, value, badge, onClick, active = false, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-haspopup="true"
      aria-expanded={active}
      className={`inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border text-xs font-medium transition-colors ${
        active
          ? 'border-brand-300 bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:border-brand-700 dark:text-brand-300'
          : 'border-gray-200/70 dark:border-gray-600/70 bg-surface dark:bg-gray-870 text-gray-700 dark:text-gray-200 hover:border-brand-300 hover:bg-brand-50 dark:hover:bg-gray-700'
      }`}
    >
      {Icon && <Icon className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0" />}
      {label && <span className="text-gray-500 dark:text-gray-400">{label}</span>}
      {value && <span className="font-semibold text-gray-900 dark:text-gray-100">{value}</span>}
      {badge != null && (
        <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-brand-600 dark:bg-brand-500 text-white text-[10px] font-semibold">
          {badge}
        </span>
      )}
      <ChevronDownIcon className="h-3 w-3 text-gray-400 flex-shrink-0" />
    </button>
  );
}

// Forecaster display modes: submissions + continuous ('both'), only the
// session submissions ('submissions'), or only the continuous forecast
// ('pending'). `short` is the compact label shown on the chip face.
const PREVIEW_MODE_OPTIONS = [
  {
    value: 'both',
    label: 'All',
    short: 'All',
    tooltip: 'Show your session submissions together with the continuous forecast',
  },
  {
    value: 'submissions',
    label: 'Submissions Only',
    short: 'Submissions',
    tooltip: 'Show only your session submissions (hide the continuous forecast)',
  },
  {
    value: 'pending',
    label: 'Continuous Only',
    short: 'Continuous',
    tooltip: 'Show only the continuous forecast (hide session submissions)',
  },
];

export default function ChartDashboardActions({
                                                startDate,
                                                endDate,
                                                model,
                                                handleApplyFilters,
                                                selectedResource,
                                                hasChartData = false,
                                                hasPendingChanges = false,
                                                horizonFilter = 'latest',
                                                availableHorizons: rawAvailableHorizons = [],
                                                previewMode = 'both',
                                                onPreviewModeChange,
                                                hasPendingPreview = false,
                                              }) {
  const dispatch = useDispatch();
  const isAdmin = useSelector(selectAuthIsAdmin);
  const isMarketOwner = useSelector(selectAuthIsMarketOwner);
  const isMarketOwnerReadOnly = useSelector(selectAuthIsMarketOwnerReadOnly);
  const isForecaster = useSelector(selectAuthIsForecaster);
  // Privileged roles get the full filter set (forecasters, model, horizon),
  // plus the phone layout and its Filters panel. Forecasters get the desktop
  // chip bar only; on phones the Dashboard shows its chart chips instead.
  const isPrivileged = isAdmin || isMarketOwner || isMarketOwnerReadOnly;
  const canUseInsights = useSelector(selectCanUseInsights);
  const usersList = useSelector(selectUsersList);
  const blurSensitive = useSelector(selectBlurSensitive);
  const selectedForecaster = useSelector((state) => state.data.selectedForecast);
  const availableHorizons = Array.isArray(rawAvailableHorizons) ? rawAvailableHorizons : [];
  const comparisonModels = useSelector(selectComparisonModels);

  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  // Which chip-bar popover is open ('insights' | 'forecasters' | 'model' | 'display' | 'horizon' | 'date' | null).
  const [openChip, setOpenChip] = useState(null);
  const chipBarRef = useRef(null);

  const AVAILABLE_MODELS = ENSEMBLE_MODELS;

  // Prevent body scroll when panel is open
  useEffect(() => {
    document.body.style.overflow = isFilterPanelOpen ? 'hidden' : 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isFilterPanelOpen]);

  // Close any open chip popover on an outside click (covers all chips at once,
  // including the date picker which runs in controlled mode).
  useEffect(() => {
    if (!openChip) return undefined;
    const handleOutside = (event) => {
      if (chipBarRef.current && !chipBarRef.current.contains(event.target)) {
        setOpenChip(null);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [openChip]);

  // Forecaster label. Only superusers receive `email` (audit A260430-API-M6 /
  // A260505-API-M2); show it to them unless "Hide sensitive info" is on,
  // otherwise fall back to the username alias (then id). We never blur — the
  // label itself reflects the privacy level.
  const displayLabel = (f) =>
    (!blurSensitive && f.email ? f.email : f.username || f.id) || '';

  // Format date for display
  const formatDateForDisplay = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
  };

  // Active values shown on the chip-bar filters.
  const modelShort = AVAILABLE_MODELS.find((m) => m.value === model)?.short ?? model;
  // Adapter for the shared ForecasterPicker. Maps usersList + the "Ensemble"
  // pseudo-entry (id=null) to the picker's id-based model and back.
  const pickerForecasters = [
    { id: '__ensemble__', name: 'Ensemble' },
    ...usersList.map((u) => ({ id: u.id, name: displayLabel(u) })),
  ];
  const pickerSelectedIds = (selectedForecaster || []).map((f) => (f.id == null ? '__ensemble__' : f.id));
  const handleForecasterPickerChange = (ids) => {
    const chosen = ids == null ? pickerForecasters : pickerForecasters.filter((f) => ids.includes(f.id));
    dispatch(setSelectedForecast(chosen.map((f) =>
      f.id === '__ensemble__' ? { id: null, name: 'Ensemble' } : { id: f.id, name: f.name }
    )));
  };
  const horizonLabelText = horizonFilter === 'latest' ? 'Latest' : formatHorizonLabel(horizonFilter);
  const previewModeShort = PREVIEW_MODE_OPTIONS.find((o) => o.value === previewMode)?.short ?? 'All';
  // Horizon keyword for the desktop header caption, highlighted in brand
  // color. On phones the Dashboard's chart header (forecasters) or the
  // chart's own caption (other roles) shows it instead.
  const horizonKeyword =
    horizonFilter === 'latest'
      ? 'latest'
      : Number(horizonFilter) === 0 ? 'intraday' : `D+${horizonFilter}`;
  const unit = resourceUnitMeta(selectedResource?.units).unit;
  const horizonOptionClass = (active) => (
    `px-2.5 py-1 text-xs font-semibold rounded-md border transition-all ${
      active
        ? 'border-brand-400 bg-brand-50 dark:bg-brand-900/30 dark:border-brand-600 text-brand-700 dark:text-brand-300'
        : 'border-gray-200/70 dark:border-gray-600/70 text-gray-600 dark:text-gray-400 hover:border-brand-300'
    }`
  );

  return (
    <div className="w-full">
      {/* Mobile: Compact Layout with Filter Button. Privileged roles only;
          forecasters get the Dashboard's chart chips and ChartOptionsSheet
          instead (the admin phase will unify them). Its "Chart Filters"
          button is the only way to open the side panel below. */}
      {isPrivileged && (
        <div className="lg:hidden space-y-4">
          {/* Inactive resource warning */}
          {selectedResource?.is_active === false && (
            <div className="flex items-center gap-2 px-3 py-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <svg className="h-5 w-5 text-yellow-600 dark:text-yellow-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.618 5.984A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016zM12 9v2m0 4h.01" />
              </svg>
              <p className="text-yellow-700 dark:text-yellow-300 font-semibold text-sm">Inactive Resource</p>
            </div>
          )}

          {/* Current Selections Display */}
          <div className="space-y-2 px-1 pt-1">
            {/* Forecasters Info */}
            {Array.isArray(selectedForecaster) && selectedForecaster.length > 0 && (
              <div className="flex items-center gap-2">
                <UsersIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  <span className="text-gray-900 dark:text-gray-100">{selectedForecaster.length}</span> forecaster{selectedForecaster.length !== 1 ? 's' : ''} selected
                </span>
              </div>
            )}

            {/* Date Range Info */}
            {startDate && endDate && (
              <div className="flex items-center gap-2">
                <CalendarIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  <span className="text-gray-900 dark:text-gray-100">{formatDateForDisplay(startDate)} - {formatDateForDisplay(endDate)}</span>
                </span>
              </div>
            )}
          </div>

          {/* Filter Button */}
          <button
            onClick={() => setIsFilterPanelOpen(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-gray-200/70 dark:border-gray-600/70 bg-surface dark:bg-gray-870 text-gray-700 dark:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600 transition-colors font-medium text-sm"
          >
            <FunnelIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            <span>Chart Filters</span>
          </button>

          {/* Apply Button */}
          <CustomButton
            caption="Apply Filters"
            icon="search"
            size="medium"
            onClick={handleApplyFilters}
            className={`w-full${hasPendingChanges ? ' !bg-gray-100 hover:!bg-gray-100 dark:!bg-gray-700 dark:hover:!bg-gray-700' : ''}`}
          />
        </div>
      )}

      {/* Desktop: chip-bar filter toolbar in the chart-card header.
          Title and horizon caption on the left, filter chips on the right.
          Each chip shows only its active value; Model/Horizon/Date open an
          inline popover and auto-apply, Forecasters opens its picker popover. */}
      {/* The title block keeps its natural width (caption split over two
          fixed lines) and the chips stack beside it as the card narrows.
          wrap-reverse fills rows bottom-up, so the overflow (the date chip
          first) lands on the title row and the filters keep full rows below
          it, instead of the date wrapping alone under a full row. */}
      <div ref={chipBarRef} className="hidden lg:flex items-start justify-between gap-x-6">
        <div className="flex-none">
          <h2 className="text-[15px] leading-5 font-semibold tracking-tight text-gray-900 dark:text-gray-100">Forecast vs measured</h2>
          <p className="mt-0.5 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
            {horizonFilter === 'latest' ? 'Showing the ' : 'Showing '}
            <span className="font-semibold italic text-brand-600 dark:text-brand-400">{horizonKeyword}</span>
            {horizonFilter === 'latest' ? ' forecast for each time' : '-horizon forecasts'}
            <span className="block">{unit} · times in UTC</span>
          </p>
        </div>

        {/* Filter chips */}
        <div className="flex min-w-0 flex-1 flex-wrap-reverse items-center justify-end gap-2">
          {/* Ensemble insights (superusers and market owners) */}
          {canUseInsights && (
            <InsightsMenu
              open={openChip === 'insights'}
              onToggle={() => setOpenChip(openChip === 'insights' ? null : 'insights')}
            />
          )}

          {/* Inactive resource warning */}
          {selectedResource?.is_active === false && (
            <span className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20 text-xs font-semibold text-yellow-700 dark:text-yellow-300">
              <svg className="h-3.5 w-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.618 5.984A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016zM12 9v2m0 4h.01" />
              </svg>
              Inactive
            </span>
          )}

          {/* Forecasters → shared popover multi-select picker */}
          {isPrivileged && (
            <ForecasterPicker
              forecasters={pickerForecasters}
              selectedIds={pickerSelectedIds}
              onChange={handleForecasterPickerChange}
              open={openChip === 'forecasters'}
              onOpenChange={(v) => setOpenChip(v ? 'forecasters' : null)}
            />
          )}

          {/* Model chip → inline popover (primary model + link to comparison) */}
          {isPrivileged && (
            <div className="relative">
              <FilterChip
                icon={CogIcon}
                label="Model"
                value={modelShort + (comparisonModels.length > 0 ? ` +${comparisonModels.length}` : '')}
                active={openChip === 'model'}
                onClick={() => setOpenChip(openChip === 'model' ? null : 'model')}
              />
              {openChip === 'model' && (
                <div className="absolute right-0 z-50 mt-2 w-60 rounded-lg bg-white dark:bg-gray-870 border border-gray-200/70 dark:border-gray-600/70 shadow-lg p-1.5">
                  <p className="px-2 py-1 text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500">
                    Primary model
                  </p>
                  {AVAILABLE_MODELS.map((m) => {
                    const isPrimary = model === m.value;
                    return (
                      <button
                        key={m.value}
                        type="button"
                        onClick={() => {
                          dispatch(setModel({ model: m.value }));
                          if (comparisonModels.includes(m.value)) {
                            dispatch(setComparisonModels(comparisonModels.filter((v) => v !== m.value)));
                          }
                          setOpenChip(null);
                        }}
                        className={`w-full flex items-center justify-between px-2 py-1.5 rounded text-sm ${
                          isPrimary
                            ? 'bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 font-semibold'
                            : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        <span>{m.label}</span>
                        <span className="text-gray-400 dark:text-gray-500 text-xs">{m.short}</span>
                      </button>
                    );
                  })}
                  <div className="mt-1 border-t border-gray-100 dark:border-gray-700 pt-1">
                    <p className="px-2 py-1 text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500">
                      Compare
                    </p>
                    {AVAILABLE_MODELS.filter((m) => m.value !== model).map((m) => {
                      const isComparison = comparisonModels.includes(m.value);
                      return (
                        <button
                          key={m.value}
                          type="button"
                          onClick={() => {
                            dispatch(setComparisonModels(
                              isComparison
                                ? comparisonModels.filter((v) => v !== m.value)
                                : [...comparisonModels, m.value]
                            ));
                          }}
                          className={`w-full flex items-center justify-between px-2 py-1.5 rounded text-sm ${
                            isComparison
                              ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 font-semibold'
                              : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                        >
                          <span>{m.label}</span>
                          <span className="text-xs">{isComparison ? '✓' : '+ compare'}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Chart-display chip → inline popover (forecaster pending preview) */}
          {hasPendingPreview && (
            <div className="relative">
              <FilterChip
                icon={EyeIcon}
                label="Show"
                value={previewModeShort}
                active={openChip === 'display'}
                onClick={() => setOpenChip(openChip === 'display' ? null : 'display')}
              />
              {openChip === 'display' && (
                <div className="absolute right-0 z-50 mt-2 w-48 rounded-lg bg-white dark:bg-gray-870 border border-gray-200/70 dark:border-gray-600/70 shadow-lg p-1.5">
                  {PREVIEW_MODE_OPTIONS.map((opt) => {
                    const isActive = previewMode === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        title={opt.tooltip}
                        onClick={() => { onPreviewModeChange?.(opt.value); setOpenChip(null); }}
                        className={`w-full text-left px-2 py-1.5 rounded text-sm ${
                          isActive
                            ? 'bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 font-semibold'
                            : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Horizon chip → inline popover */}
          {(isPrivileged || isForecaster) && (
            <div className="relative">
              <FilterChip
                icon={ClockIcon}
                label="Horizon"
                value={horizonLabelText}
                active={openChip === 'horizon'}
                onClick={() => setOpenChip(openChip === 'horizon' ? null : 'horizon')}
              />
              {openChip === 'horizon' && (
                <div className="absolute right-0 z-50 mt-2 w-52 rounded-lg bg-white dark:bg-gray-870 border border-gray-200/70 dark:border-gray-600/70 shadow-lg p-2">
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      type="button"
                      onClick={() => { dispatch(setHorizonFilter('latest')); setOpenChip(null); }}
                      className={horizonOptionClass(horizonFilter === 'latest')}
                    >
                      Latest
                    </button>
                    {availableHorizons.map((h) => (
                      <button
                        key={h}
                        type="button"
                        onClick={() => { dispatch(setHorizonFilter(String(h))); setOpenChip(null); }}
                        className={horizonOptionClass(horizonFilter === String(h))}
                      >
                        {formatHorizonLabel(h)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Date range chip — controlled so it coordinates with the others */}
          <DateRangePicker
            variant="chip"
            open={openChip === 'date'}
            onOpenChange={(v) => setOpenChip(v ? 'date' : null)}
            startDate={startDate}
            endDate={endDate}
            maxHorizon={availableHorizons.length > 0 ? Math.max(1, ...availableHorizons) : 1}
            onDateRangeChange={(newStartDate, newEndDate) => {
              dispatch(setDateRange({ startDate: newStartDate, endDate: newEndDate }));
            }}
          />
        </div>
      </div>

      {/* Filter Side Panel (Mobile) */}
      {isFilterPanelOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setIsFilterPanelOpen(false)} />
          <div className="fixed inset-y-0 right-0 w-full sm:w-96 bg-white dark:bg-gray-870 shadow-xl z-50 overflow-y-auto" style={SLIDE_OVER_TOP_OFFSET_STYLE}>
            <div className="sticky top-0 bg-surface dark:bg-gray-870 border-b border-gray-200/70 dark:border-gray-600/70 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Chart Filters</h2>
              <button
                onClick={() => setIsFilterPanelOpen(false)}
                className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-6">
              {/* Forecaster Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Forecasters
                </label>
                <ForecasterPicker
                  forecasters={pickerForecasters}
                  selectedIds={pickerSelectedIds}
                  onChange={handleForecasterPickerChange}
                />
              </div>

              {/* Date Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Date Range
                </label>
                <DateRangePicker
                  startDate={startDate}
                  endDate={endDate}
                  maxHorizon={availableHorizons.length > 0 ? Math.max(1, ...availableHorizons) : 1}
                  onDateRangeChange={(newStartDate, newEndDate) => {
                    dispatch(setDateRange({ startDate: newStartDate, endDate: newEndDate }));
                  }}
                />
              </div>

              {/* Forecast Horizon */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Forecast Horizon
                </label>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => dispatch(setHorizonFilter('latest'))}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md border transition-all ${
                      horizonFilter === 'latest'
                        ? 'border-brand-400 bg-brand-50 dark:bg-brand-900/30 dark:border-brand-600 text-brand-700 dark:text-brand-300'
                        : 'border-gray-200/70 dark:border-gray-600/70 text-gray-600 dark:text-gray-400 hover:border-brand-300'
                    }`}
                  >
                    Latest
                  </button>
                  {availableHorizons.map(h => (
                    <button
                      key={h}
                      onClick={() => dispatch(setHorizonFilter(String(h)))}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-md border transition-all ${
                        horizonFilter === String(h)
                          ? 'border-brand-400 bg-brand-50 dark:bg-brand-900/30 dark:border-brand-600 text-brand-700 dark:text-brand-300'
                          : 'border-gray-200/70 dark:border-gray-600/70 text-gray-600 dark:text-gray-400 hover:border-brand-300'
                      }`}
                    >
                      {formatHorizonLabel(h)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Model Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Model
                </label>
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-2">
                    {AVAILABLE_MODELS.map((m) => (
                      <button
                        key={m.value}
                        type="button"
                        onClick={() => {
                          dispatch(setModel({ model: m.value }));
                          if (comparisonModels.includes(m.value)) {
                            dispatch(setComparisonModels(comparisonModels.filter((v) => v !== m.value)));
                          }
                        }}
                        className={`px-3 py-1.5 text-xs font-semibold rounded-md border transition-all ${
                          model === m.value
                            ? 'border-brand-400 bg-brand-50 dark:bg-brand-900/30 dark:border-brand-600 text-brand-700 dark:text-brand-300'
                            : 'border-gray-200/70 dark:border-gray-600/70 text-gray-600 dark:text-gray-400 hover:border-brand-300'
                        }`}
                      >
                        {m.short}
                      </button>
                    ))}
                  </div>
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500">Compare</p>
                  <div className="flex flex-wrap gap-2">
                    {AVAILABLE_MODELS.filter((m) => m.value !== model).map((m) => {
                      const on = comparisonModels.includes(m.value);
                      return (
                        <button
                          key={m.value}
                          type="button"
                          onClick={() => dispatch(setComparisonModels(on ? comparisonModels.filter((v) => v !== m.value) : [...comparisonModels, m.value]))}
                          className={`px-3 py-1.5 text-xs font-semibold rounded-md border transition-all ${
                            on
                              ? 'border-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 dark:border-emerald-600 text-emerald-700 dark:text-emerald-300'
                              : 'border-gray-200/70 dark:border-gray-600/70 text-gray-600 dark:text-gray-400 hover:border-brand-300'
                          }`}
                        >
                          {m.short}{on ? ' ✓' : ''}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Done Button */}
              <button
                onClick={() => setIsFilterPanelOpen(false)}
                className="w-full px-4 py-2.5 rounded-md border border-brand-500 bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 hover:bg-brand-100 dark:hover:bg-brand-900/50 text-sm font-medium transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </>
      )}

    </div>
  );
}
